import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, PLAN_LABELS } from "@/lib/perms";
import { money, statusLabel, statusColor } from "@/lib/format";
import { shareRaffle } from "@/lib/share";
import DashboardLayout from "@/components/DashboardLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Ticket, DollarSign, Clock, TrendingUp, Plus, ArrowRight, ExternalLink, ArrowUpCircle, Trash2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] animate-fade-up">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tint}`}><Icon size={20} /></div>
      <p className="text-sm text-neutral-500">{label}</p>
      <p className="font-display font-extrabold text-2xl mt-1">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = can(user, "manage_raffles");
  const [metrics, setMetrics] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delRaffle, setDelRaffle] = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [myReq, setMyReq] = useState(null);
  const [upForm, setUpForm] = useState({ message: "", requested_active_raffles: "", requested_numbers: "", phone: "", email: "", plan_choice: "full" });
  const [sendingUp, setSendingUp] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, r] = await Promise.all([api.get("/dashboard/metrics"), api.get("/raffles")]);
        setMetrics(m.data);
        setRaffles(r.data);
        if (!m.data.is_super_admin) {
          api.get("/upgrade-request/mine").then((res) => setMyReq(res.data.request)).catch(() => {});
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const submitUpgrade = async () => {
    if (!upForm.phone.trim() || !upForm.email.trim()) { toast.error("Teléfono y correo son obligatorios"); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(upForm.email.trim())) { toast.error("Ingresa un correo válido"); return; }
    setSendingUp(true);
    try {
      const fullMessage = `Plan solicitado: ${upForm.plan_choice === "custom" ? "Custom" : "Full"}\n${upForm.message || "Solicitud de ampliación"}\n\nContacto — Teléfono: ${upForm.phone.trim()} · Correo: ${upForm.email.trim()}`;
      await api.post("/upgrade-request", {
        message: fullMessage,
        requested_active_raffles: upForm.requested_active_raffles ? Number(upForm.requested_active_raffles) : null,
        requested_numbers: upForm.requested_numbers ? Number(upForm.requested_numbers) : null,
      });
      toast.success("Solicitud enviada al super-administrador");
      setUpgradeOpen(false);
      const res = await api.get("/upgrade-request/mine");
      setMyReq(res.data.request);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally { setSendingUp(false); }
  };

  const pendingUpgrade = myReq && myReq.status === "pending";

  const reload = async () => {
    const [m, r] = await Promise.all([api.get("/dashboard/metrics"), api.get("/raffles")]);
    setMetrics(m.data);
    setRaffles(r.data);
  };

  const confirmDeleteRaffle = async () => {
    if (!delRaffle) return;
    setDelLoading(true);
    try {
      await api.delete(`/raffles/${delRaffle.id}`);
      toast.success("Rifa eliminada");
      setDelRaffle(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar la rifa");
    } finally { setDelLoading(false); }
  };

  const cur = metrics?.currency || "₡";
  const planLabel = PLAN_LABELS[metrics?.plan] || "Gratis";

  return (
    <DashboardLayout title="Panel">
      {metrics && !metrics.is_super_admin && (
        <div data-testid="limits-banner" className="mb-6 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center flex-shrink-0"><Ticket size={20} /></div>
          <div className="flex-1 text-sm">
            {(metrics.max_active_raffles ?? 1) >= 9999 ? (
              <>
                <p className="font-semibold text-neutral-800">Tienes <b>rifas ilimitadas</b> · Plan {planLabel}</p>
                <p className="text-neutral-500">Máximo {(metrics.max_numbers_per_raffle ?? 10000).toLocaleString("es-CR")} números por rifa.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-neutral-800">
                  Vas <b>{metrics.active_raffles}/{metrics.max_active_raffles}</b> rifas activas
                  {metrics.active_raffles >= metrics.max_active_raffles && <span className="text-amber-600"> · límite alcanzado</span>}
                </p>
                <p className="text-neutral-500">Máximo {metrics.max_numbers_per_raffle} números por rifa. Mejora tu plan para más rifas, asistentes e historial.</p>
              </>
            )}
          </div>
          {metrics.plan !== "free" ? (
            <span data-testid="full-access-badge" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#E6F7FE] text-[#0092CC] whitespace-nowrap">Plan {planLabel}</span>
          ) : pendingUpgrade ? (
            <span data-testid="upgrade-pending" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">Solicitud en revisión</span>
          ) : (
            <Button data-testid="request-upgrade-btn" variant="outline" size="sm" onClick={() => { setUpForm({ message: "", requested_active_raffles: "", requested_numbers: "", phone: "", email: "", plan_choice: "full" }); setUpgradeOpen(true); }} className="rounded-xl whitespace-nowrap">
              <ArrowUpCircle size={15} className="mr-1" /> Solicitar mejora de plan
            </Button>
          )}
        </div>
      )}

      {metrics?.pending_orders > 0 && (
        <div data-testid="pending-banner" className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-4 flex items-center gap-3 animate-fade-up">
          <div className="h-11 w-11 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0"><Clock size={22} /></div>
          <div className="flex-1">
            <p className="font-display font-bold text-amber-900">Tienes {metrics.pending_orders} {metrics.pending_orders === 1 ? "pago" : "pagos"} por verificar</p>
            <p className="text-sm text-amber-700">Revisa los comprobantes para confirmar los números.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric icon={Ticket} label="Rifas activas" value={metrics?.active_raffles ?? "—"} tint="bg-[#E6F7FE] text-[#00AEEF]" />
        <Metric icon={TrendingUp} label="Números vendidos" value={metrics?.numbers_sold ?? "—"} tint="bg-neutral-900/5 text-neutral-800" />
        <Metric icon={DollarSign} label="Ingresos" value={metrics ? money(metrics.revenue, cur) : "—"} tint="bg-amber-50 text-amber-600" />
        <Metric icon={Clock} label="Por verificar" value={metrics?.pending_orders ?? "—"} tint="bg-sky-50 text-sky-600" />
      </div>

      {metrics?.stats && (
        <div data-testid="adv-stats-panel" className="mb-8 rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center"><TrendingUp size={18} /></div>
            <h2 className="font-display font-bold text-lg">Estadísticas avanzadas</h2>
            <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#E6F7FE] text-[#0092CC]">Pro</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
            <div className="rounded-xl bg-neutral-50 p-4"><p className="text-xs text-neutral-500">Tasa de venta</p><p className="font-display font-extrabold text-2xl mt-1">{metrics.stats.sell_through}%</p></div>
            <div className="rounded-xl bg-neutral-50 p-4"><p className="text-xs text-neutral-500">Ticket promedio</p><p className="font-display font-extrabold text-2xl mt-1">{money(metrics.stats.avg_ticket, cur)}</p></div>
            <div className="rounded-xl bg-neutral-50 p-4"><p className="text-xs text-neutral-500">Pedidos aprobados</p><p className="font-display font-extrabold text-2xl mt-1">{metrics.stats.approved_orders}</p></div>
          </div>
          {metrics.stats.by_raffle?.length > 0 && (
            <div className="space-y-3" data-testid="adv-stats-by-raffle">
              <p className="text-sm font-semibold text-neutral-700">Ingresos por evento</p>
              {metrics.stats.by_raffle.map((r) => {
                const max = metrics.stats.by_raffle[0].revenue || 1;
                const w = Math.max(4, Math.round((r.revenue / max) * 100));
                return (
                  <div key={r.id} data-testid={`stats-raffle-${r.id}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="truncate font-medium text-neutral-700">{r.title}</span>
                      <span className="text-neutral-500 whitespace-nowrap ml-2">{money(r.revenue, cur)} · {r.pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-neutral-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#00AEEF]" style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Mis rifas</h2>
        <Button data-testid="new-raffle-btn" onClick={() => navigate("/dashboard/raffles/nueva")} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl">
          <Plus size={16} className="mr-1" /> Nueva rifa
        </Button>
      </div>

      {loading ? (
        <p className="text-neutral-400">Cargando...</p>
      ) : raffles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mx-auto mb-4"><Ticket size={26} /></div>
          <h3 className="font-display font-bold text-lg">Aún no tienes rifas</h3>
          <p className="text-neutral-500 mt-1 mb-5">Crea tu primera rifa y empieza a vender números.</p>
          <Button data-testid="empty-new-raffle-btn" onClick={() => navigate("/dashboard/raffles/nueva")} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl">Crear rifa</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {raffles.map((r) => (
            <div key={r.id} data-testid={`raffle-card-${r.id}`} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-shadow animate-fade-up">
              <div className="h-32 bg-neutral-100 relative">
                {r.image_urls?.[0] ? <img src={r.image_urls[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-300"><Ticket size={40} /></div>}
                <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(r.status)}`}>{statusLabel(r.status)}</span>
                {r.pending_count > 0 && (
                  <span data-testid={`pending-badge-${r.id}`} className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500 text-white shadow flex items-center gap-1">
                    <Clock size={12} /> {r.pending_count} por verificar
                  </span>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display font-bold text-lg truncate">{r.title}</h3>
                <p className="text-sm text-neutral-500 mt-1">{money(r.price_per_number, r.currency)} por número</p>
                {r.seed_code && <p className="text-[11px] text-neutral-400 mt-0.5">Código semilla: <span className="font-mono font-semibold text-neutral-500 tracking-wider">{r.seed_code}</span></p>}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-neutral-500 mb-1">
                    <span>{r.progress?.sold ?? 0} / {r.progress?.total ?? r.total_numbers} vendidos</span>
                    <span className="font-semibold">{r.progress?.pct ?? 0}%</span>
                  </div>
                  <Progress value={r.progress?.pct ?? 0} className="h-2" />
                </div>
                <div className="flex gap-2 mt-4">
                  <Link to={`/dashboard/raffles/${r.id}`} className="flex-1"><Button data-testid={`manage-raffle-${r.id}`} variant="outline" className="w-full rounded-xl">Gestionar <ArrowRight size={15} className="ml-1" /></Button></Link>
                  {r.status !== "draft" && <a href={`/r/${r.id}`} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" className="rounded-xl" data-testid={`view-public-${r.id}`}><ExternalLink size={16} /></Button></a>}
                  {r.status !== "draft" && <Button variant="ghost" size="icon" className="rounded-xl text-[#0092CC] hover:bg-[#E6F7FE]" data-testid={`share-raffle-${r.id}`} title="Compartir enlace" onClick={() => shareRaffle(r.id, r.title)}><Share2 size={16} /></Button>}
                  {canManage && ((r.progress?.sold ?? 0) === 0 || user?.is_super_admin) && <Button variant="ghost" size="icon" className="rounded-xl text-red-500 hover:bg-red-50" data-testid={`delete-raffle-${r.id}`} onClick={() => setDelRaffle(r)}><Trash2 size={16} /></Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Solicitar ampliación de límites</DialogTitle>
            <DialogDescription>Cuéntale al super-administrador qué necesitas. Recibirá tu solicitud por correo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Rifas activas que necesitas</Label>
                <Input data-testid="upgrade-raffles" type="number" min={1} value={upForm.requested_active_raffles} onChange={(e) => setUpForm({ ...upForm, requested_active_raffles: e.target.value })} placeholder={`Actual: ${metrics?.max_active_raffles ?? 1}`} className="mt-1 rounded-lg" />
              </div>
              <div>
                <Label className="text-sm">Números por rifa que necesitas</Label>
                <Input data-testid="upgrade-numbers" type="number" min={1} value={upForm.requested_numbers} onChange={(e) => setUpForm({ ...upForm, requested_numbers: e.target.value })} placeholder={`Actual: ${metrics?.max_numbers_per_raffle ?? 50}`} className="mt-1 rounded-lg" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Plan solicitado</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" data-testid="upgrade-plan-custom" onClick={() => setUpForm({ ...upForm, plan_choice: "custom" })}
                  className={`rounded-lg border p-2 text-sm transition-colors ${upForm.plan_choice === "custom" ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Plan Custom</button>
                <button type="button" data-testid="upgrade-plan-full" onClick={() => setUpForm({ ...upForm, plan_choice: "full" })}
                  className={`rounded-lg border p-2 text-sm transition-colors ${upForm.plan_choice === "full" ? "border-[#00AEEF] bg-[#E6F7FE] text-[#0092CC] font-semibold" : "border-neutral-200 text-neutral-500"}`}>Plan Full</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Teléfono <span className="text-red-500">*</span></Label>
                <Input data-testid="upgrade-phone" value={upForm.phone} onChange={(e) => setUpForm({ ...upForm, phone: e.target.value })} placeholder="Ej: 8888-7777" className="mt-1 rounded-lg" />
              </div>
              <div>
                <Label className="text-sm">Correo <span className="text-red-500">*</span></Label>
                <Input data-testid="upgrade-email" type="email" value={upForm.email} onChange={(e) => setUpForm({ ...upForm, email: e.target.value })} placeholder="tucorreo@ejemplo.com" className="mt-1 rounded-lg" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Mensaje (opcional)</Label>
              <Textarea data-testid="upgrade-message" value={upForm.message} onChange={(e) => setUpForm({ ...upForm, message: e.target.value })} rows={3} placeholder="Ej: Voy a lanzar una rifa grande el próximo mes y necesito más números." className="mt-1 rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeOpen(false)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="send-upgrade-btn" onClick={submitUpgrade} disabled={sendingUp} className="rounded-xl bg-[#00AEEF] hover:bg-[#0092CC]">{sendingUp ? "Enviando..." : "Enviar solicitud"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!delRaffle}
        onOpenChange={(v) => !v && setDelRaffle(null)}
        title="¿Desea realmente realizar esta acción?"
        description={delRaffle ? `Se eliminará la rifa "${delRaffle.title}" con todos sus números y pedidos. Esta acción no se puede deshacer.` : ""}
        confirmText="Sí, eliminar rifa"
        onConfirm={confirmDeleteRaffle}
        loading={delLoading}
      />
    </DashboardLayout>
  );
}
